/**
 * cart-upsell.js
 * Thin interactivity layer over the server-rendered `cart-upsell` snippet.
 * All product data, pricing, and copy already come from Liquid (metafield +
 * metaobject driven, Market-aware). This file only handles:
 *   - showing the single highest-priority (non-banner) panel
 *   - dismiss-for-session
 *   - add/swap cart actions (with Recharge selling_plan support, and line
 *     item properties for products that require one — see collectProperties)
 *   - upsell attribution: hidden _upsell_* line item properties recording
 *     which module converted, on which surface, and what it replaced — see
 *     collectAttribution
 *   - re-binding whenever #cart-upsell-root's content gets (re)inserted
 *
 * WORKS IN TWO CONTEXTS:
 *   - Cart drawer (sections/cart-drawer.liquid), target element id "cart-drawer"
 *   - Standalone cart page (sections/main-cart.liquid), target element id
 *     "main-cart-section"
 * The snippet passes which one applies via the data-refresh-target-id
 * attribute on #cart-upsell-root, set from the refresh_target_id param on
 * the {% render 'cart-upsell' %} call in each parent template.
 *
 * BINDING STRATEGY:
 * The surrounding markup can be replaced by more than one thing — the theme's
 * own AJAX cart flow when items are added from a product page, this script's
 * own refresh after an upsell click, and potentially Recharge/Stretch
 * customizations too. Rather than guessing every event name that might signal
 * a change (fragile — easy to miss one, which is exactly what caused buttons
 * to be unclickable until a full page reload), a MutationObserver watches the
 * DOM directly and re-binds/re-displays whenever #cart-upsell-root's content
 * changes, regardless of what caused it.
 */
(function () {
  'use strict';

  var ROOT_ID = 'cart-upsell-root';
  var SECTION_ID = 'cart-upsell';
  var DISMISS_KEY = 'cartUpsellDismissed';
  var DEFAULT_TARGET_ID = 'cart-drawer';

  var state = { index: 0, busy: false, panels: [] };

  function root() {
    return document.getElementById(ROOT_ID);
  }

  function collectPanels() {
    var r = root();
    if (!r) return { modules: [], banners: [] };
    var all = Array.prototype.slice.call(r.querySelectorAll('[data-cu-panel]'));
    var modules = all.filter(function (p) { return p.getAttribute('data-layout') !== 'banner'; });
    var banners = all.filter(function (p) { return p.getAttribute('data-layout') === 'banner'; });
    modules.sort(function (a, b) {
      return parseInt(a.getAttribute('data-priority'), 10) - parseInt(b.getAttribute('data-priority'), 10);
    });
    return { modules: modules, banners: banners };
  }

  function removeDots() {
    var r = root();
    if (!r) return;
    var existing = r.querySelectorAll('.cu-dots');
    existing.forEach(function (d) { d.remove(); });
  }

  function applyDisplay() {
    var r = root();
    if (!r) return;

    if (sessionStorage.getItem(DISMISS_KEY) === '1') {
      r.querySelectorAll('[data-layout]:not([data-layout="banner"])').forEach(function (p) {
        p.style.display = 'none';
      });
      removeDots();
      return;
    }

    var collected = collectPanels();
    state.panels = collected.modules;

    // Show ALL eligible modules stacked (priority order); no pagination,
    // no dots, no auto-rotation.
    collected.modules.forEach(function (p) {
      p.style.display = '';
    });
    collected.banners.forEach(function (p) { p.style.display = ''; });

    removeDots();
  }

  function setBusy(b) {
    state.busy = b;
    var r = root();
    if (r) r.classList.toggle('cu-busy', b);
  }

  function bindClicks() {
    var r = root();
    if (!r || r.dataset.cuBound) return;
    r.addEventListener('click', handleClick);
    r.addEventListener('change', handlePropertyChange);
    r.dataset.cuBound = '1';
  }

  function handlePropertyChange(e) {
    var sel = e.target.closest('.cu-prop-select');
    if (!sel || !sel.value) return;
    sel.classList.remove('cu-prop-select--error');
    var errorEl = sel.parentNode.querySelector('.cu-prop-error');
    if (errorEl) errorEl.hidden = true;
  }

  // Called whenever we detect #cart-upsell-root exists with fresh content
  // (initial load, theme-driven replacement, or our own refresh).
  function attach() {
    var r = root();
    if (!r) return;
    bindClicks();
    state.index = 0;
    applyDisplay();
  }

  function refreshSection() {
    return fetch('/?section_id=' + SECTION_ID, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var freshRoot = doc.getElementById(ROOT_ID);
        var currentRoot = root();
        if (freshRoot && currentRoot) {
          currentRoot.innerHTML = freshRoot.innerHTML;
          attach();
        }
      })
      .catch(function (err) { console.error('[cart-upsell] section refresh failed', err); });
  }

  // Generic "refresh the whole parent context" function. Works for both the
  // cart drawer (target id "cart-drawer") and the standalone cart page
  // (target id "main-cart-section"), because the target element id comes
  // from data-refresh-target-id, set by the snippet's refresh_target_id
  // param — see cart-upsell.liquid.
  function refreshWholeContext() {
    var r = root();
    var sectionId = r && r.dataset.drawerSectionId;
    var targetId = (r && r.dataset.refreshTargetId) || DEFAULT_TARGET_ID;

    if (!sectionId) return refreshSection(); // fallback: cart_drawer_section_id wasn't passed

    return fetch('/?section_id=' + sectionId, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var freshTarget = doc.getElementById(targetId);
        var liveTarget = document.getElementById(targetId);
        if (freshTarget && liveTarget) {
          liveTarget.innerHTML = freshTarget.innerHTML;
          attach(); // #cart-upsell-root now lives inside the fresh markup — rebind it
        } else {
          return refreshSection(); // fallback
        }
      })
      .catch(function (err) {
        console.error('[cart-upsell] whole-context refresh failed, falling back to panel-only refresh', err);
        return refreshSection();
      });
  }

  function swapLine(lineKey, variantId, sellingPlanId, properties) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(function () {
      return addVariant(variantId, sellingPlanId, properties);
    });
  }

  function addVariant(variantId, sellingPlanId, properties) {
    var item = { id: variantId, quantity: 1 };
    if (sellingPlanId) item.selling_plan = sellingPlanId;
    // Line item properties (e.g. the Annual Subscription's delivery choice).
    // Same shape the theme's product form posts, so a product added via an
    // upsell merges with one added from its product page rather than creating
    // a second, differently-labelled line.
    if (properties && Object.keys(properties).length) item.properties = properties;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] })
    });
  }

  // ---------------------------------------------------------------------
  // Line item properties
  // Collects every .cu-prop-select inside the clicked button's OWN tile or
  // card, so two tiles in the same module can each carry their own field
  // without reading each other's. Returns null when a required field is
  // unanswered — the caller treats that as "block the add, show the error".
  // ---------------------------------------------------------------------
  function collectProperties(btn) {
    var scope = btn.closest('.cu-tile') || btn.closest('[data-cu-panel]');
    if (!scope) return {};

    var selects = scope.querySelectorAll('.cu-prop-select');
    var props = {};
    var valid = true;

    for (var i = 0; i < selects.length; i++) {
      var sel = selects[i];
      var name = sel.getAttribute('data-cu-prop-name');
      var value = sel.value;
      var errorEl = sel.parentNode.querySelector('.cu-prop-error');

      if (!value) {
        if (sel.hasAttribute('required')) {
          valid = false;
          sel.classList.add('cu-prop-select--error');
          if (errorEl) errorEl.hidden = false;
        }
        continue;
      }

      sel.classList.remove('cu-prop-select--error');
      if (errorEl) errorEl.hidden = true;
      if (name) props[name] = value;
    }

    if (!valid) {
      var firstBad = scope.querySelector('.cu-prop-select--error');
      if (firstBad) firstBad.focus();
      return null;
    }
    return props;
  }

  // ---------------------------------------------------------------------
  // UPSELL ATTRIBUTION
  //
  // Builds the hidden line item properties that let an order be traced back
  // to the offer that produced it. Underscore-prefixed keys are skipped by
  // Shopify's cart, checkout and email templates, so the customer never sees
  // them — but they persist on the order and show in Admin and the API.
  //
  // WHY THIS HAPPENS AT CLICK TIME: on a swap the trigger line is DELETED
  // moments later. Once it's gone the order has no record it ever existed, so
  // "upgraded from the 1000mg pen" cannot be reconstructed afterwards. The
  // only moment both products are known is right here.
  //
  // Reading from the button's own data-* attributes (rendered by Liquid) and
  // not from a cart lookup is deliberate — swapLine() removes before it adds,
  // so a lookup would already be too late.
  //
  // _upsell_from_* means "the line this offer hung off":
  //   action=swap -> the product that was replaced
  //   action=add  -> the product that triggered the offer (bundle modules)
  // _upsell_action is what tells the two apart.
  function collectAttribution(btn, isSwap) {
    var moduleHandle = btn.getAttribute('data-upsell-module');
    if (!moduleHandle) return {};

    var r = root();
    var props = {
      _upsell_module: moduleHandle,
      _upsell_action: isSwap ? 'swap' : 'add',
      _upsell_surface: (r && r.dataset.cuSurface) || 'drawer'
    };

    var fromVariant = btn.getAttribute('data-upsell-from-variant');
    var fromTitle = btn.getAttribute('data-upsell-from-title');
    var fromPrice = btn.getAttribute('data-upsell-from-price');

    if (fromVariant) props._upsell_from_variant = fromVariant;
    // Trimmed because line item property values are length-capped and a long
    // product title adds nothing past the point of recognition.
    if (fromTitle) props._upsell_from_title = fromTitle.slice(0, 120);
    if (fromPrice) props._upsell_from_price = fromPrice;

    return props;
  }

  function handleClick(e) {
    var btn = e.target.closest('[data-action]');
    if (!btn || state.busy) return;
    var action = btn.getAttribute('data-action');

    if (action === 'dismiss') {
      sessionStorage.setItem(DISMISS_KEY, '1');
      applyDisplay();
      return;
    }

    if (action !== 'upgrade') return;
    e.preventDefault();

    var variantId = btn.getAttribute('data-variant');
    var sellingPlanId = btn.getAttribute('data-selling-plan') || null;
    var swapLineKey = btn.getAttribute('data-swap-line') || null;
    if (!variantId) {
      console.warn('[cart-upsell] Missing variant ID on upsell CTA — check the metaobject\'s recommended_products.');
      return;
    }

    var properties = collectProperties(btn);
    if (properties === null) return;   // required selection missing — inline error shown

    // Attribution rides alongside the customer-facing properties (e.g. the
    // Annual Subscription's delivery choice) in the same /cart/add.js payload.
    var attribution = collectAttribution(btn, Boolean(swapLineKey));
    Object.keys(attribution).forEach(function (k) { properties[k] = attribution[k]; });

    setBusy(true);
    var work = swapLineKey
      ? swapLine(swapLineKey, variantId, sellingPlanId, properties)
      : addVariant(variantId, sellingPlanId, properties);

    work
      .then(refreshWholeContext)
      .catch(function (err) { console.error('[cart-upsell] action failed', err); })
      .finally(function () { setBusy(false); });
  }

  // ---------------------------------------------------------------------
  // Watch the DOM directly instead of relying on any specific cart-update
  // event name. Whenever #cart-upsell-root's subtree changes (added fresh,
  // or its innerHTML replaced by the theme/Recharge/our own code), (re)bind.
  // Observe whichever container exists on this page — drawer or cart page.
  // ---------------------------------------------------------------------
  var observer;
  var pending = false;

  function scheduleAttach() {
    if (pending) return;
    pending = true;
    setTimeout(function () {
      pending = false;
      attach();
    }, 30);
  }

  function startObserving() {
    var target = document.getElementById('cart-drawer')
      || document.getElementById('main-cart-section')
      || document.body;
    observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          scheduleAttach();
          return;
        }
      }
    });
    observer.observe(target, { childList: true, subtree: true });
  }

  function init() {
    attach();          // covers the case root is already present at load
    startObserving();  // covers every later (re)insertion, from anywhere
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();